import { useEffect, useState } from 'react';
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';
import Lightbox from '../components/Lightbox';
import { getCollection, optimizeImage } from '../lib/cms';

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const allReferences = [
    {
      id: 'euc-syd',
      title: 'EUC-SYD',
      subtitle: 'Automationsudstyr til skolebrug',
      desc: 'Udvikling, opbygning og programmering af automationsudstyr og robotanlæg til undervisning i mekatronik, elkonstruktion og robotteknologi.',
      logo: 'https://assets.auxodev.dk/assets/1cab479d-9d2b-49e7-a4f6-be9602b123ca/euc-logo.jpg',
      tags: ['Automation', 'Robotanlæg', 'Skoleudstyr'],
    },
    {
      id: 'fr-petersen',
      title: 'Fr. Petersen Maskinfabrik A/S',
      subtitle: 'Maskinkonstruktion, montage & indkøring',
      desc: 'Konstruktion, 2D/3D modellering, FEM-styrkeberegninger samt mekanisk og elektrisk montage og indkøring af delkomponenter.',
      logo: 'https://assets.auxodev.dk/assets/1cab479d-9d2b-49e7-a4f6-be9602b123ca/frpetersen-600-150x150.jpg',
      tags: ['Maskinkonstruktion', 'Montage', 'FEM analyse'],
    },
    {
      id: 'holtec',
      title: 'Holtec Automatic',
      subtitle: 'Styretavler, elkonstruktion & automation',
      desc: 'Samarbejde om el-diagrammer i PC Schematik, opbygning af styretavler og automationsløsninger til industri- og procesanlæg.',
      logo: 'https://assets.auxodev.dk/assets/1cab479d-9d2b-49e7-a4f6-be9602b123ca/logo-e1444055127362-1.png',
      tags: ['Elkonstruktion', 'Styretavler', 'PC Schematik'],
    },
    {
      id: 'gibotech',
      title: 'Gibotech',
      subtitle: 'Robotanlæg & specialmaskiner',
      desc: 'Konstruktionsassistance, mekanisk design og udvikling af automationsceller og avancerede robotanlæg til produktionsvirksomheder.',
      logo: 'https://assets.auxodev.dk/assets/1cab479d-9d2b-49e7-a4f6-be9602b123ca/2dee4a-01b2b211d5ea4244916831dd44ab4a7b-mv2.jpg',
      tags: ['Robotter', 'Specialmaskiner', 'CAD konstruktion'],
    },
    {
      id: 'easyrobotics',
      title: 'EasyRobotics',
      subtitle: 'Mobile cobot- og robotplatforme',
      desc: 'Mekanisk konstruktion, CAD-udvikling og optimering af mobile cobot-stationer, emnefødere og fleksible automationsplatforme.',
      logo: 'https://assets.auxodev.dk/assets/1cab479d-9d2b-49e7-a4f6-be9602b123ca/2dee4a-e4773b90d5d84e7d9a79c755c2e3476b-mv2.jpg',
      tags: ['Cobot platforme', 'Mekanik', 'Automation'],
    },
    {
      id: 'linak',
      title: 'LINAK A/S',
      subtitle: 'Produktionsudstyr & specialfiksturer',
      desc: 'Udvikling og fremstilling af præcise produktionshjælpemidler, montagefiksturer og udstyr til kvalitetssikring i montagelinjer.',
      logo: 'https://assets.auxodev.dk/assets/1cab479d-9d2b-49e7-a4f6-be9602b123ca/linak.jpg',
      tags: ['Produktionsudstyr', 'Fiksturer', 'Optimering'],
    },
    {
      id: 'ck-mt',
      title: 'CK-Mt ApS (CK Maskinteknik)',
      subtitle: 'Laserskæring & maskinbearbejdning',
      desc: 'Samarbejde omkring laserskæring, CNC-drejning/fræsning, pladebukning og mekaniske delkomponenter til industriløsninger.',
      logo: 'https://assets.auxodev.dk/assets/1cab479d-9d2b-49e7-a4f6-be9602b123ca/2dee4a-cff9eab60c19441fbcd0a9e6e378026e-mv2.jpg',
      tags: ['Laserskæring', 'CNC bearbejdning', 'Pladearbejde'],
    },
  ];

  useEffect(() => {
    async function loadProjects() {
      const data = await getCollection('projects');
      setProjects(data || []);
    }
    loadProjects();
  }, []);

  const openLightbox = (images, index = 0) => {
    setLightboxImages(images);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const filteredProjects = projects.filter((p) => {
    if (selectedFilter === 'all') return true;
    const cat = p.terms?.['project-category'] || [];
    return cat.includes(selectedFilter);
  });

  return (
    <div className="bg-[#0b1017] text-slate-100 min-h-screen">
      <SEO
        title="Referencer | JARCon"
        description="Referencer og gennemførte industriprojekter: EUC-SYD, Fr. Petersen Maskinfabrik A/S, LINAK m.fl."
      />

      <PageHeader
        badge="Referencer & Cases"
        title="Projekter & Referencer"
        subtitle="Se udvalgte gennemførte opgaver inden for automation, maskinkonstruktion, tavlebygning og specialudstyr."
        breadcrumbs={[{ label: 'Projekter' }]}
        ctaText="Drøft dit projekt med os"
        ctaLink="/kontakt"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* All 7 References from original jarcon.dk/referencer */}
        <div className="mb-14 p-6 sm:p-8 rounded-2xl bg-[#0e1622] border border-slate-800">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8 pb-4 border-b border-slate-800/80">
            <div>
              <span className="text-xs font-open-sans text-cyan-400 uppercase tracking-widest font-semibold block mb-1">
                Officielle Referencer
              </span>
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-white">
                Dokumenteret erfaring og samarbejdspartnere
              </h2>
            </div>
            <p className="text-xs sm:text-sm text-slate-400 max-w-md">
              Her er alle referencer fra JARCon, der vidner om solid mekatronisk, maskinteknisk og automationsmæssig ekspertise.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {allReferences.map((ref) => (
              <div
                key={ref.id}
                className="p-6 rounded-xl bg-[#090f18] border border-slate-800/90 hover:border-cyan-500/50 transition-all flex flex-col justify-between group"
              >
                <div>
                  {/* Clean white logo container so all logos are crisp & visible */}
                  <div className="h-20 w-full rounded-lg bg-white p-3 flex items-center justify-center mb-5 shadow-sm border border-slate-200">
                    <img
                      src={ref.logo}
                      alt={ref.title}
                      className="max-h-14 max-w-full object-contain"
                    />
                  </div>

                  <h3 className="font-display text-lg font-bold text-white group-hover:text-cyan-400 transition-colors">
                    {ref.title}
                  </h3>
                  <span className="text-xs font-semibold text-cyan-400 block mb-2 font-open-sans">
                    {ref.subtitle}
                  </span>
                  <p className="text-xs text-slate-300 leading-relaxed font-sans">
                    {ref.desc}
                  </p>
                </div>

                <div className="pt-4 mt-4 border-t border-slate-800/70 flex flex-wrap gap-1.5">
                  {ref.tags.map((tag, idx) => (
                    <span
                      key={idx}
                      className="text-[10px] font-open-sans uppercase tracking-wider px-2 py-0.5 rounded bg-slate-800 text-slate-400"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Filter Pills */}
        <div className="flex items-center gap-2 overflow-x-auto pb-4 mb-10 scrollbar-none">
          {[
            { id: 'all', label: 'Alle projekter' },
            { id: 'automatiske-maskiner', label: 'Automatiske maskiner' },
            { id: 'automation-robotter', label: 'Robotter & Automation' },
            { id: 'mekanisk-konstruktion', label: 'Mekanisk konstruktion' },
            { id: 'elkonstruktion', label: 'Elkonstruktion' },
            { id: 'produktionsudstyr', label: 'Produktionsudstyr' },
            { id: 'problemlosninger', label: 'Problemløsninger' },
          ].map((cat) => (
            <button
              key={cat.id}
              type="button"
              onClick={() => setSelectedFilter(cat.id)}
              className={`px-4 py-2 rounded-lg text-xs font-open-sans font-medium whitespace-nowrap transition-all ${
                selectedFilter === cat.id
                  ? 'bg-cyan-500 text-white font-bold shadow-md shadow-cyan-950/50'
                  : 'bg-slate-900 text-slate-200 hover:text-white border border-slate-800 hover:border-slate-700'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Projects List */}
        <div className="space-y-12">
          {filteredProjects.map((p) => {
            const slugStr = typeof p.slug === 'string' ? p.slug : p.slug?._ || p.slug?.current || p.id || '';
            const featImg = p.featured_image && p.featured_image[0] ? optimizeImage(p.featured_image[0], 800) : null;
            const gallery = p.gallery || [];

            return (
              <article
                key={p.id || slugStr}
                id={slugStr}
                className="p-8 sm:p-10 rounded-2xl bg-[#0e1622] border border-slate-800 hover:border-slate-700 transition-all scroll-mt-28"
              >
                <div className="space-y-6">
                  {/* Left info column */}
                  <div>
                    <div className="flex flex-wrap items-center gap-3 mb-2 font-open-sans text-xs">
                      {p.client && (
                        <span className="px-2.5 py-1 rounded bg-cyan-950/80 text-cyan-300 border border-cyan-500/30 font-semibold">
                          Kunde: {p.client}
                        </span>
                      )}
                      {p.project_date && (
                        <span className="text-slate-500">
                          Dato: {p.project_date}
                        </span>
                      )}
                    </div>
                    <h3 className="font-display text-2xl sm:text-3xl font-bold text-white">
                      {p.title}
                    </h3>
                    {p.short_intro && (
                      <p className="mt-2 text-slate-300 text-sm sm:text-base leading-relaxed">
                        {p.short_intro}
                      </p>
                    )}
                  </div>

                  {/* Problem / Solution / Result Trio */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 border-t border-slate-800/80">
                    {p.problem && (
                      <div className="p-4 rounded-lg bg-[#090e15] border border-slate-800 space-y-1.5">
                        <span className="text-xs font-open-sans text-amber-400 uppercase tracking-wider font-semibold block">
                          Udfordring
                        </span>
                        <p className="text-xs text-slate-300 leading-relaxed">
                          {p.problem}
                        </p>
                      </div>
                    )}

                    {p.solution && (
                      <div className="p-4 rounded-lg bg-[#090e15] border border-slate-800 space-y-1.5">
                        <span className="text-xs font-open-sans text-cyan-400 uppercase tracking-wider font-semibold block">
                          Løsning
                        </span>
                        <p className="text-xs text-slate-300 leading-relaxed">
                          {p.solution}
                        </p>
                      </div>
                    )}

                    {p.result && (
                      <div className="p-4 rounded-lg bg-[#090e15] border border-slate-800 space-y-1.5">
                        <span className="text-xs font-open-sans text-emerald-400 uppercase tracking-wider font-semibold block">
                          Resultat
                        </span>
                        <p className="text-xs text-slate-300 leading-relaxed">
                          {p.result}
                        </p>
                      </div>
                    )}
                  </div>

                  {p.related_services && (
                    <div className="text-xs font-open-sans text-slate-400">
                      <span className="text-slate-500">Relaterede ydelser:</span> {p.related_services}
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </div>

      <Lightbox
        images={lightboxImages}
        currentIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </div>
  );
}
